import React, { useState, useRef, useEffect } from "react";
import { countriesAndCodes } from "../../data/countriesAndCodes";

interface CountryDropdownProps {
  selectedCountry: string;
  handleCountryChange: (value: string) => void;
}

const CountryDropdown: React.FC<CountryDropdownProps> = ({
  selectedCountry,
  handleCountryChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleOptionClick = (code: string) => {
    handleCountryChange(code);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredCountries = countriesAndCodes.filter((country) =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCountryName =
    countriesAndCodes.find((c) => c.code === selectedCountry)?.name || "Select Country";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="z-20" ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="w-full rounded bg-white md:p-1 focus:outline-none md:px-1 py-2 text-left flex items-center text-[14px]"
      >
        {selectedCountryName}
        <svg
          className="ml-2 w-4 h-4"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="#dc0069"
        >
          <polygon points="12,16 6,8 18,8" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          <input
            type="text"
            placeholder="Search country..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-4 py-2 border-b border-gray-200 text-sm focus:outline-none"
          />
          <ul className="max-h-48 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <li
                  key={country.code}
                  onClick={() => handleOptionClick(country.code)}
                  className={`px-4 py-2 cursor-pointer hover:bg-halo-pink-bg hover:text-halo-pink text-[14px] ${
                    selectedCountry === country.code ? "bg-halo-pink-bg text-halo-pink" : ""
                  }`}
                >
                  {country.name}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-400 text-sm">No results found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountryDropdown;